import { useMemo } from "react";
import ScrollArea from "../ScrollArea.jsx";
import PageHero from "../PageHero.jsx";
import TraceTable from "../TraceTable.jsx";
import { KernelBlock } from "../kernelPrimitives/index.jsx";
import { NOTE_CLASS } from "../../lib/ui.js";
import { useQuery } from "../../lib/dataClient.js";
import { signalDefByCommand, useSignalCatalog } from "../../lib/signalCatalog.js";
import { hasSensitivePermission, useMyPermissions } from "../../lib/permissions.js";
import { PERMISSIONS } from "../../lib/permissionKeys.js";

const GHOST_BTN = "inline-flex min-h-[24px] items-center cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[9px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]";

function Chip({ children }) {
  return <span className="border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[10px] text-[color:var(--muted-soft)]">{children}</span>;
}

const PAGE_LIMIT = 200;

// Per-signal lens — the detail for one signal. Signals are READ from the backend
// (signal_tags), so this is a scoped trace view: the signal scope is already in
// nav.scopePath, so the standard getTraces query (which INNER-joins SignalTags
// on that scope) returns exactly the prompts that fired this signal — no
// in-browser scan, no window scrubber. Every row fired this signal, so no
// per-row badge is needed.
export default function SignalDetailBody({ nav, slot, ctx }) {
  const scopePath = nav?.scopePath || [];
  const terminal = scopePath.length ? scopePath[scopePath.length - 1] : null;
  const command = (terminal?.dim === "signals" || terminal?.dim === "signal")
    ? (terminal.ids?.[0] ?? terminal.id ?? null)
    : null;
  // BE-sourced: resolve once the catalog is hydrated (re-runs when `ready` flips).
  const { ready, denied, error } = useSignalCatalog();
  const def = useMemo(() => (command && ready ? signalDefByCommand(command) : null), [command, ready]);
  // A signal in the catalog — enabled OR disabled — can have firings worth
  // showing: one disabled AFTER it fired keeps its signal_tags until they TTL
  // out. So render the firing view whenever the signal resolves; a disabled one
  // just carries a banner (it isn't running, so no NEW firings will accrue).
  const resolved = !!def;
  const disabled = resolved && !def.deployedEnabled;

  // Omit the enriched prompt text (ai_sessions sensitive) for a role without
  // that read, or the column policy 403s the whole firing-prompts query and
  // the signal looks like it never fired. Fail-CLOSED so a non-sensitive role
  // never requests the column (no transient 403 during caps-load).
  const caps = useMyPermissions();
  const canReadSensitivePrompts = hasSensitivePermission(caps, PERMISSIONS.AI_SESSIONS_READ_SENSITIVE);

  const { data: traces = [], loading } = useQuery(
    "getTraces",
    { scopePath, range: ctx?.range, limit: PAGE_LIMIT, sort: "newest", canReadSensitivePrompts },
    { enabled: resolved },
  );

  // Firing-rate chart for this one signal over the range, server time-bucketed.
  // getSignalFiringSeries drops the signal scope step and applies the namespace
  // filter from `commands`, so passing the full scopePath is correct.
  const { data: firing } = useQuery(
    "getSignalFiringSeries",
    { scopePath, commands: resolved && command ? [command] : [], range: ctx?.range },
    { enabled: resolved && !!command },
  );
  const firingLayers = firing?.layers || [];
  const hasFiring = firingLayers.some((l) => (l.series || []).some((v) => v > 0));

  const onSelectTrace = (id) => { ctx?.traceLog?.onSelectTrace?.(id); slot?.onExpandDetail?.(); };
  const showDefinition = () => { ctx?.onShowDefinition?.(); slot?.onExpandDetail?.(); };

  if (!resolved) {
    const title = denied || error ? "Signals unavailable" : !ready ? "Loading…" : "Unknown signal";
    const note = denied
      ? "You don’t have permission to view signals."
      : error
        ? "Couldn’t load signals. Try again shortly."
        : !ready
          ? "Loading the signal registry…"
          : "This signal isn’t in the catalog for this tenant.";
    return (
      <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]" aria-busy={!ready && !denied && !error}>
        <PageHero eyebrow="Signal" title={title} detail={command ? `/${command}` : ""} />
        <span className={NOTE_CLASS}>{note}</span>
      </ScrollArea>
    );
  }

  const total = Number(traces?.total) || traces.length || 0;

  return (
    <ScrollArea data-content-scroll className="min-h-0 h-full" contentClassName="grid gap-[12px] pb-[18px]">
      <PageHero
        eyebrow="Signal"
        title={def.label}
        detail={def.description || `/${def.command}`}
        controls={
          <button type="button" onClick={showDefinition} className={GHOST_BTN}>
            View definition
          </button>
        }
        metrics={[
          { label: "Fired", value: String(total), note: "prompts firing this signal in scope" },
        ]}
      />
      <div className="flex flex-wrap gap-[5px]">
        <Chip>/{def.command}</Chip>
        <Chip>{def.namespace}</Chip>
        <Chip>{disabled ? "disabled" : "built-in"}</Chip>
      </div>
      {disabled ? (
        <span className={NOTE_CLASS}>
          This signal is disabled. No new findings will appear, but historical findings remain visible until they expire.
        </span>
      ) : null}
      <span className={NOTE_CLASS}>
        {total} prompt{total === 1 ? "" : "s"} fired this signal in the current scope.
      </span>
      {hasFiring ? (
        <KernelBlock spec={{ kind: "chart", mode: "line", layers: firingLayers, height: 150, range: ctx?.range, title: "Firing rate", compactStandingTooltip: true }} />
      ) : null}
      <TraceTable
        rows={traces}
        selectedTraceId={ctx?.traceLog?.selectedTraceId || ""}
        onSelectTrace={onSelectTrace}
        showEndpoint={ctx?.traceLog?.showEndpoint}
        loading={loading}
        emptyMessage="No prompts fired this signal in this scope."
      />
    </ScrollArea>
  );
}
